import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { isNotEmptyArray } from "Services/functions";
import { fetchGet } from "Services/api";
import {
  getOffersByProfileData,
  getOffersByTransmitterData,
} from "Components/Stats/Data/Offres/Transmetteur";
import Margin from "Components/Stats/Margin";
import NivoPie from "Components/Stats/Charts/NivoPie";

/**
 * Composant 'OffersByTransmitter'.
 *
 * Ce composant affiche deux graphiques qui représentent les provenances des offres du portail universitaire :
 * - Le premier graphique représente la répartition des offres par profil des utilisateurs sur l'université.
 * - Le deuxième graphique représente la répartition des offres par transmetteur sur l'université.
 *
 * Les données sont récupérées à partir de l'API et formatées pour les graphiques à l'aide de fonctions spécifiques.
 *
 * - {@link getOffersByProfileData} : Fonction qui formate les données pour le graphique des offres par profil.
 * - {@link getOffersByTransmitterData} : Fonction qui formate les données pour le graphique des offres par transmetteur.
 *
 * - {@link NivoPie} : Composant générique de graphique en camembert implémenté avec la librairie Nivo.
 *
 *
 * Les graphiques utilisent le composant NivoPie pour afficher les données.
 *
 * @param {Object} props - Les propriétés du composant.
 * @param {Object} props.auth - Les informations d'authentification de l'utilisateur. (depuis Redux)
 * @param {Object} props.secondaryTables - Les données des tables secondaires. (depuis Redux)
 * @param {Object} props.states - Les états des offres. (depuis Redux)
 * @param {string} props.universityName - Le nom de l'université pour laquelle afficher les statistiques.
 * @returns {JSX.Element} Les graphiques de répartition des offres par profil et par transmetteur.
 *
 *
 * @example
 * // Utilisation du composant 'OffersByTransmitter' dans un autre composant.
 * import OffersByTransmitter from './OffersByTransmitter';
 * // ...
 * <OffersByTransmitter
 *   auth={authData}
 *   secondaryTables={secondaryTableData}
 *   states={statesData}
 *   universityName="Nom de l'université"
 * />
 */
const OffersByTransmitter = (props) => {
  const [offersByProfile, setOffersByProfile] = useState([]);
  const [offersByTransmitter, setOffersByTransmitter] = useState([]);

  /**
   * Récupération des données pour les offres par transmetteur
   */
  const offersByTransmitterRequest = async () => {
    let data = await fetchGet(
      "/offers/stats/count/transmitter",
      props.auth.token,
      {
        // Filtre état: publié
        "state[]": props.states.statesData.find(
          (state) => state.statusLabel === "Publié"
        ).id,
      }
    );
    setOffersByProfile(getOffersByProfileData(data));
    setOffersByTransmitter(getOffersByTransmitterData(data));
  };

  useEffect(() => {
    if (isNotEmptyArray(props.states.statesData)) offersByTransmitterRequest();
  }, [props.states.statesData]);

  return (
    <React.Fragment>
      <div
        style={{
          //? Sets default height for Nivo responsive pie
          height: "500px",
        }}
      >
        <NivoPie
          data={offersByProfile}
          title={
            "Provenance des offres du portail universitaire par profil sur l'université " +
            props.universityName
          }
          labelName="Profil"
          displayPercentage
        />
      </div>
      <Margin position="bottom" size="" />
      <NivoPie
        data={offersByTransmitter}
        title={
          "Provenance des offres par transmetteur sur l'université " +
          props.universityName
        }
        labelName="Transmetteur"
        displayPercentage
        legendWidth={130}
      />
      <Margin position="bottom" size="" />
    </React.Fragment>
  );
};

OffersByTransmitter.propTypes = {
  auth: PropTypes.object.isRequired,
  secondaryTables: PropTypes.object.isRequired,
  states: PropTypes.object.isRequired,
  universityName: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  states: state.states,
  auth: state.auth,
  secondaryTables: state.secondaryTables,
});

export default connect(mapStateToProps)(OffersByTransmitter);
